import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Typography,
    Button,
    Alert,
    AppBar,
    Toolbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Fab,
    Badge,
    IconButton,
    Tooltip,
    CircularProgress,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import {
    ShoppingCart as ShoppingCartIcon,
    Close as CloseIcon,
    Sync as SyncIcon,
    WifiOff as WifiOffIcon,
    CloudDone as CloudDoneIcon,
} from "@mui/icons-material";
import { stockService } from "../../services/stockService";
import { PaymentUpdateDialog } from "../../components/orders/PaymentUpdateDialog";
import { QuickToolLinks } from "../../components/common/QuickToolLinks";
import { POS_PAYMENT_METHODS } from "../../utils/paymentMethods";
import { SUBMIT_SHORTCUT_LABEL, isSubmitShortcut } from "../../utils/keyboard";
import { KeyHint } from "../../components/common/KeyHint";
import { Order } from "../../types";

// Import modular components
import { Cart } from "./components/Cart";
import { ProductSearch } from "./components/ProductSearch";
import { PaymentDialog } from "./components/PaymentDialog";
import { QuantityDialog } from "./components/QuantityDialog";
import { QuickProductDialog } from "./components/QuickProductDialog";
import { ReceiptModal } from "./components/ReceiptModal";
import { POSPayment } from "./types";

// Import Redux hooks
import { usePOSRedux, useFilteredProducts } from "./hooks/usePOSRedux";

function formatSyncAge(ts: number): string {
    const secs = Math.floor((Date.now() - ts) / 1000);
    if (secs < 60) return "just now";
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    return `${Math.floor(secs / 86400)}d ago`;
}

interface SyncStatusBadgeProps {
    lastSyncAt: number | null;
    usingCachedData: boolean;
    syncing: boolean;
    onRefresh: () => void;
}

const SyncStatusBadge: React.FC<SyncStatusBadgeProps> = ({
    lastSyncAt,
    usingCachedData,
    syncing,
    onRefresh,
}) => {
    const [, forceUpdate] = React.useReducer((x) => x + 1, 0);
    React.useEffect(() => {
        const interval = setInterval(forceUpdate, 30_000);
        return () => clearInterval(interval);
    }, []);

    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {usingCachedData && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "warning.main" }}>
                    <WifiOffIcon fontSize="small" />
                    <Typography variant="caption" fontWeight="medium">Offline</Typography>
                </Box>
            )}
            {lastSyncAt && !usingCachedData && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.secondary" }}>
                    <CloudDoneIcon fontSize="small" sx={{ opacity: 0.6 }} />
                    <Typography variant="caption">Synced {formatSyncAge(lastSyncAt)}</Typography>
                </Box>
            )}
            <Tooltip title={syncing ? "Syncing…" : "Sync products & customers"}>
                <span>
                    <IconButton size="small" onClick={onRefresh} disabled={syncing}>
                        {syncing
                            ? <CircularProgress size={16} />
                            : <SyncIcon fontSize="small" />
                        }
                    </IconButton>
                </span>
            </Tooltip>
        </Box>
    );
};

export const POSSystem: React.FC = () => {
    // Redux-based POS management
    const posRedux = usePOSRedux();
    const { filteredProducts } = useFilteredProducts();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [mobileCartOpen, setMobileCartOpen] = useState(false);

    const cartItemCount = posRedux.cart.reduce(
        (sum, item) => sum + item.quantity,
        0,
    );

    const quantityInputRef = useRef<HTMLInputElement>(null);

    // Focus quantity input when dialog opens
    useEffect(() => {
        if (posRedux.state.quantityDialogOpen && quantityInputRef.current) {
            const timer = setTimeout(() => {
                if (quantityInputRef.current) {
                    quantityInputRef.current.focus();
                    quantityInputRef.current.select();
                }
            }, 50);
            return () => clearTimeout(timer);
        }
    }, [posRedux.state.quantityDialogOpen]);

    const [stockWarnings, setStockWarnings] = useState<string[]>([]);
    const pendingPayment = useRef<{ payments: POSPayment[] } | null>(null);

    // Order still owing money after the cashier confirms delivery — mirrors
    // OrderList.tsx's onDeliveredUnpaid pattern so the balance can be
    // settled immediately instead of leaving a delivered-but-unpaid order.
    const [underpaidOrder, setUnderpaidOrder] = useState<Order | null>(null);

    const handleAddProductFromHistory = (productId: string, unitPrice: number) => {
        const product =
            posRedux.state.products.find((p) => p.product_id === productId);
        if (!product) return;
        posRedux.handleAddToCart(product, 1);
        posRedux.handleUpdateCartItemPrice(productId, unitPrice);
    };

    const handleCheckout = (deliveryCharge: number, discount: number) => {
        posRedux.handleSetDeliveryCharge(deliveryCharge);
        posRedux.handleSetDiscount(discount);
        posRedux.handleOpenPaymentDialog();
    };

    const handlePayment = async (payments: POSPayment[]) => {
        const cart = posRedux.cart;
        const items = cart.map((item) => ({
            product_id: item.product.product_id,
            quantity: item.quantity,
        }));
        try {
            const stockCheck =
                await stockService.checkMultipleStockAvailability(items);
            if (!stockCheck.all_available) {
                const warnings = stockCheck.items
                    .filter((i) => !i.available)
                    .map((i) => i.reason || "Insufficient stock");
                pendingPayment.current = { payments };
                posRedux.handleClosePaymentDialog();
                setStockWarnings(warnings);
                return;
            }
        } catch {
            // If stock check fails, proceed normally (don't block the sale)
        }
        posRedux.handleProcessOrder(payments);
    };

    const handleDeliveryConfirmYes = async () => {
        const orderId = posRedux.state.receiptOrder?.order_id;
        if (!orderId) return;
        const freshOrder = await posRedux.handleConfirmMarkDelivered(orderId);
        if (!freshOrder) return;
        // The receipt was already shown (it comes before this question);
        // if the order still owes money, settle it now.
        if (Number(freshOrder.amount_due || 0) > 0) {
            setUnderpaidOrder(freshOrder);
        }
    };

    return (
        <Box
            sx={{
                // The layout already pads the page on a phone.
                p: { xs: 0, sm: 3 },
                height: "100vh",
                display: "flex",
                flexDirection: "column",
            }}
        >
            {/* Header — the tools drop below the title on a phone */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                    mb: { xs: 2, sm: 3 },
                }}
            >
                <Typography variant="h4" component="h1" sx={{ fontSize: { xs: "1.75rem", sm: "2.125rem" } }}>
                    Point of Sale
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <QuickToolLinks toolIds={["daily-cash-flow-analyzer"]} />
                    <SyncStatusBadge
                        lastSyncAt={posRedux.state.lastSyncAt}
                        usingCachedData={posRedux.state.usingCachedData}
                        syncing={posRedux.state.syncing}
                        onRefresh={posRedux.handleRefreshProducts}
                    />
                </Box>
            </Box>

            {posRedux.state.error && (
                <Alert
                    severity={posRedux.state.usingCachedData ? "warning" : "error"}
                    sx={{ mb: 2 }}
                    onClose={posRedux.handleClearMessages}
                    action={posRedux.state.usingCachedData ? (
                        <Button color="inherit" size="small" onClick={posRedux.handleRefreshProducts}>
                            Retry sync
                        </Button>
                    ) : undefined}
                >
                    {posRedux.state.error}
                </Alert>
            )}

            {posRedux.state.success && (
                <Alert
                    severity="success"
                    sx={{ mb: 2 }}
                    onClose={posRedux.handleClearMessages}
                    action={
                        posRedux.state.lastCompletedOrder && (
                            <Button
                                color="inherit"
                                size="small"
                                onClick={posRedux.handleShowLastReceipt}
                            >
                                View Receipt
                            </Button>
                        )
                    }
                >
                    {posRedux.state.success}
                </Alert>
            )}

            <Box sx={{ display: "flex", gap: 3, flex: 1, minHeight: 0 }}>
                {/* Product Search & Selection — full width on mobile */}
                <Box sx={{ flex: isMobile ? 1 : 2, minWidth: 0 }}>
                    <ProductSearch
                        products={filteredProducts}
                        searchTerm={posRedux.state.productSearch}
                        onSearchChange={posRedux.handleProductSearch}
                        onProductSelect={posRedux.handleOpenQuantityDialog}
                        onQuickAddProduct={
                            posRedux.handleOpenQuickProductDialog
                        }
                        searchInputRef={posRedux.searchInputRef}
                    />
                </Box>

                {/* Cart & Checkout — desktop only (inline) */}
                {!isMobile && (
                    <Box sx={{ flex: 1, minWidth: 400 }}>
                        <Cart
                            cart={posRedux.cart}
                            selectedCustomer={posRedux.state.selectedCustomer}
                            onCustomerChange={posRedux.handleSetSelectedCustomer}
                            onQuantityUpdate={posRedux.handleUpdateCartQuantity}
                            onPriceUpdate={posRedux.handleUpdateCartItemPrice}
                            onRemoveFromCart={posRedux.handleRemoveFromCart}
                            onClearCart={posRedux.handleClearCart}
                            onCheckout={handleCheckout}
                            onCustomerCreate={posRedux.handleAddCustomer}
                            total={posRedux.cartTotal}
                            loading={posRedux.state.loading}
                            initialDeliveryCharge={posRedux.state.deliveryCharge}
                            initialDiscount={posRedux.state.discount}
                            onDeliveryChargeChange={posRedux.handleSetDeliveryCharge}
                            onDiscountChange={posRedux.handleSetDiscount}
                            onAddProductFromHistory={handleAddProductFromHistory}
                            checkoutShortcut
                        />
                    </Box>
                )}
            </Box>

            {/* Mobile: floating cart button */}
            {isMobile && (
                <Fab
                    color="primary"
                    onClick={() => setMobileCartOpen(true)}
                    sx={{
                        position: "fixed",
                        bottom: 24,
                        right: 24,
                        zIndex: theme.zIndex.fab,
                    }}
                >
                    <Badge
                        badgeContent={cartItemCount}
                        color="error"
                        max={99}
                    >
                        <ShoppingCartIcon />
                    </Badge>
                </Fab>
            )}

            {/* Mobile: full-screen cart dialog */}
            <Dialog
                open={isMobile && mobileCartOpen}
                onClose={() => setMobileCartOpen(false)}
                fullScreen
            >
                <AppBar sx={{ position: "relative" }} color="default" elevation={0}>
                    <Toolbar variant="dense" sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
                        <Typography variant="h6" sx={{ flex: 1 }}>
                            Cart
                            {cartItemCount > 0 && (
                                <Typography
                                    component="span"
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ ml: 1 }}
                                >
                                    ({cartItemCount} item{cartItemCount !== 1 ? "s" : ""})
                                </Typography>
                            )}
                        </Typography>
                        <IconButton
                            edge="end"
                            onClick={() => setMobileCartOpen(false)}
                            aria-label="close cart"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Toolbar>
                </AppBar>
                <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}>
                    <Cart
                        cart={posRedux.cart}
                        selectedCustomer={posRedux.state.selectedCustomer}
                        onCustomerChange={posRedux.handleSetSelectedCustomer}
                        onQuantityUpdate={posRedux.handleUpdateCartQuantity}
                        onPriceUpdate={posRedux.handleUpdateCartItemPrice}
                        onRemoveFromCart={posRedux.handleRemoveFromCart}
                        onClearCart={posRedux.handleClearCart}
                        onCheckout={(deliveryCharge, discount) => {
                            setMobileCartOpen(false);
                            handleCheckout(deliveryCharge, discount);
                        }}
                        onCustomerCreate={posRedux.handleAddCustomer}
                        total={posRedux.cartTotal}
                        loading={posRedux.state.loading}
                        initialDeliveryCharge={posRedux.state.deliveryCharge}
                        initialDiscount={posRedux.state.discount}
                        onDeliveryChargeChange={posRedux.handleSetDeliveryCharge}
                        onDiscountChange={posRedux.handleSetDiscount}
                        onAddProductFromHistory={handleAddProductFromHistory}
                    />
                </DialogContent>
            </Dialog>

            <PaymentDialog
                open={posRedux.state.paymentDialogOpen}
                onClose={posRedux.handleClosePaymentDialog}
                total={Math.max(
                    posRedux.cartTotal +
                        posRedux.state.deliveryCharge -
                        posRedux.state.discount,
                    0,
                )}
                deliveryCharge={posRedux.state.deliveryCharge}
                discount={posRedux.state.discount}
                subtotal={posRedux.cartTotal}
                onPayment={handlePayment}
                loading={posRedux.state.loading}
            />

            {/* Post-sale, after the receipt is closed: confirm whether to mark the new (PENDING) order delivered */}
            <Dialog
                open={posRedux.state.deliveryConfirmOpen}
                onClose={posRedux.handleSkipDeliveryConfirm}
                maxWidth="xs"
                fullWidth
                // Ctrl/⌘+Enter = Mark as Delivered (Esc = Not Now), like the rest of checkout.
                onKeyDownCapture={(e) => {
                    if (!isSubmitShortcut(e.nativeEvent) || !e.currentTarget.contains(e.target as Node)) return;
                    e.preventDefault();
                    e.stopPropagation();
                    if (!posRedux.state.loading) handleDeliveryConfirmYes();
                }}
            >
                <DialogTitle>Mark as Delivered?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Order {posRedux.state.receiptOrder?.order_number} was
                        created as <strong>Pending</strong> — stock has been
                        reserved but not yet reduced. Mark it as delivered now
                        if the customer is taking the items with them.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={posRedux.handleSkipDeliveryConfirm}
                        disabled={posRedux.state.loading}
                    >
                        Not Now
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleDeliveryConfirmYes}
                        disabled={posRedux.state.loading}
                    >
                        {posRedux.state.loading
                            ? "Updating…"
                            : "Mark as Delivered"}
                        {!posRedux.state.loading && <KeyHint>{SUBMIT_SHORTCUT_LABEL}</KeyHint>}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Order was marked delivered but still has a balance due — settle it now */}
            <PaymentUpdateDialog
                open={!!underpaidOrder}
                onClose={() => setUnderpaidOrder(null)}
                order={underpaidOrder}
                paymentMethods={POS_PAYMENT_METHODS}
                onSuccess={() => setUnderpaidOrder(null)}
            />

            {/* Negative stock warning confirmation dialog */}
            <Dialog open={stockWarnings.length > 0} maxWidth="xs" fullWidth>
                <DialogTitle>Stock Warning</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        The following items have insufficient stock:
                    </Alert>
                    {stockWarnings.map((msg, i) => (
                        <Typography
                            key={i}
                            variant="body2"
                            sx={{ mb: 0.5, pl: 1 }}
                        >
                            • {msg}
                        </Typography>
                    ))}
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        Do you want to proceed anyway? This will result in
                        negative stock.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setStockWarnings([]);
                            pendingPayment.current = null;
                            posRedux.handleOpenPaymentDialog();
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={() => {
                            const p = pendingPayment.current;
                            setStockWarnings([]);
                            pendingPayment.current = null;
                            if (p) {
                                posRedux.handleProcessOrder(
                                    p.payments,
                                    true,
                                );
                            }
                        }}
                    >
                        Proceed Anyway
                    </Button>
                </DialogActions>
            </Dialog>

            <QuantityDialog
                open={posRedux.state.quantityDialogOpen}
                product={posRedux.state.selectedProductForQuantity}
                quantity={posRedux.state.quantityValue}
                onQuantityChange={posRedux.handleSetQuantityValue}
                isGift={posRedux.state.quantityDialogIsGift}
                onIsGiftChange={posRedux.handleSetQuantityDialogIsGift}
                onConfirm={posRedux.handleConfirmQuantity}
                onClose={posRedux.handleCloseQuantityDialog}
                inputRef={quantityInputRef}
            />

            <ReceiptModal
                open={posRedux.state.receiptModalOpen}
                onClose={posRedux.handleCloseReceiptModal}
                order={posRedux.state.receiptOrder}
                customer={posRedux.state.receiptCustomer}
                paymentMethod={posRedux.state.lastPaymentMethod}
                amountPaid={posRedux.state.lastAmountPaid}
            />

            <QuickProductDialog
                open={posRedux.state.quickProductDialogOpen}
                onClose={posRedux.handleCloseQuickProductDialog}
                searchTerm={posRedux.state.productSearch}
                onProductCreated={posRedux.handleAddProduct}
            />
        </Box>
    );
};
